/**
 * Owner: Masa Bayu
 * File: commerce-boundary.ts
 * Date:Time: 2026-09-15 19:35
 * Description: T0 commerce boundary — auto affiliate/referral, separate ledgers (≠ T3 calc).
 */
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
import { assertNoForbiddenFoundationKey } from './authority';
/**
 * LOCKED architecture direction:
 * Affiliate & Referral are platform-native, automatic, and seller-configuration-free.
 */
export const QXREAD_COMMERCE_INVARIANTS = Object.freeze({
    affiliateReferralPlatformNative: true,
    affiliateReferralAutomatic: true,
    affiliateReferralSellerConfigFree: true,
    affiliateNotPublicationTruth: true,
    referralNotPublicationTruth: true,
    rbsNotServiceFee: true,
    rbsNotAffiliate: true,
    rbsNotReferral: true,
    affiliateNotReferral: true,
    sellerWorkflowIsCreatePublishSell: true,
    affiliateRewardWithdrawable: true,
    referralRewardWithdrawable: true,
    rewardsAreBuyerFunded: true,
    sellerPriceDoesNotFundRewards: true,
});
/** Separate ledger / business concepts — never merge. */
export const QXREAD_LEDGER_CONCEPTS = [
    'seller_base_price',
    'service_fee',
    'affiliate',
    'referral',
    'rbs',
];
/** Rates locked for T3 settlement; T0 stores contract only (basis points / cents). */
export const QXREAD_COMMERCE_RATE_CONTRACT = Object.freeze({
    serviceFeeBuyerPaidBps: 450,
    affiliateAutomaticBps: 500,
    referralAutomaticBps: 500,
    rbsUsdCentsPerUniqueEbookWithSaleInMonth: 100,
});
export const QXREAD_SELLER_KINDS = ['individual', 'company', 'publisher', 'author'];
export const QXREAD_ATTRIBUTION_STATES = [
    'captured',
    'pending_order',
    'attributed',
    'void',
];
function assertSellerKind(value, label) {
    if (typeof value !== 'string' || !QXREAD_SELLER_KINDS.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_SELLER_KINDS.join(', ')}`);
    }
    return value;
}
function assertAttributionState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_ATTRIBUTION_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_ATTRIBUTION_STATES.join(', ')}`);
    }
    return value;
}
function assertLedgerConcept(value, label) {
    if (typeof value !== 'string' || !QXREAD_LEDGER_CONCEPTS.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_LEDGER_CONCEPTS.join(', ')}`);
    }
    return value;
}
/**
 * Every saleable marketplace eBook is auto-eligible for Affiliate + Referral.
 * Seller kind does not require configure/activate steps.
 */
export function evaluateAutoAffiliateReferralEligibility(input) {
    assertValidId(input.publicationId, 'QXRead publicationId');
    const sellerKind = assertSellerKind(input.sellerKind, 'QXRead sellerKind');
    if (!input.saleableOnQxread) {
        return Object.freeze({
            publicationId: input.publicationId,
            sellerKind,
            saleableOnQxread: false,
            affiliateEligible: false,
            referralEligible: false,
            reason: 'Not saleable on QXRead — Affiliate/Referral eligibility does not apply',
        });
    }
    return Object.freeze({
        publicationId: input.publicationId,
        sellerKind,
        saleableOnQxread: true,
        affiliateEligible: true,
        referralEligible: true,
        reason: 'Saleable on QXRead — Affiliate and Referral automatic; seller configuration not required',
    });
}
export function createQxreadAttributionContract(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead attribution');
    assertValidId(options.publicationId, 'QXRead attribution publicationId');
    const attributionId = options.attributionId !== undefined ? options.attributionId : createQxreadId('qxrdattr');
    assertValidId(attributionId, 'QXRead attributionId');
    let affiliateAccountId = null;
    if (options.affiliateAccountId !== undefined && options.affiliateAccountId !== null) {
        assertValidId(options.affiliateAccountId, 'QXRead affiliateAccountId');
        affiliateAccountId = options.affiliateAccountId;
    }
    let referralAccountId = null;
    if (options.referralAccountId !== undefined && options.referralAccountId !== null) {
        assertValidId(options.referralAccountId, 'QXRead referralAccountId');
        referralAccountId = options.referralAccountId;
    }
    const capturedAt = options.capturedAt ?? new Date().toISOString();
    assertTimestamp(capturedAt, 'QXRead attribution capturedAt');
    return Object.freeze({
        attributionId,
        publicationId: options.publicationId,
        affiliateAccountId,
        referralAccountId,
        state: assertAttributionState(options.state ?? 'captured', 'QXRead attribution state'),
        capturedAt,
    });
}
export function createQxreadLedgerLineContract(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead ledger line');
    if (!Number.isFinite(options.amountMinorUnits) || !Number.isInteger(options.amountMinorUnits)) {
        throw new Error('QXRead ledger amountMinorUnits must be a finite integer');
    }
    assertValidId(options.correlationId, 'QXRead ledger correlationId');
    const lineId = options.lineId !== undefined ? options.lineId : createQxreadId('qxrdled');
    assertValidId(lineId, 'QXRead ledger lineId');
    const currency = options.currency ?? 'USD';
    if (typeof currency !== 'string' || currency.trim().length === 0) {
        throw new Error('QXRead ledger currency is required');
    }
    return Object.freeze({
        lineId,
        concept: assertLedgerConcept(options.concept, 'QXRead ledger concept'),
        amountMinorUnits: options.amountMinorUnits,
        currency: currency.trim().toUpperCase(),
        correlationId: options.correlationId,
    });
}
/** RBS monthly charge predicate — separate from Service Fee / Affiliate / Referral. */
export function isRbsChargeableForUniqueEbookMonth(input) {
    return input.uniqueEbookHadCompletedSaleInMonth;
}
/**
 * Forbidden aliases — keeps ledger concepts distinct for T3 settlement.
 */
export function assertDistinctLedgerConcepts(left, right) {
    if (left === right) {
        throw new Error(`QXRead ledger concepts must remain distinct: ${left}`);
    }
}
export function assertCommerceInvariantsIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_COMMERCE_INVARIANTS)) {
        const expected = QXREAD_COMMERCE_INVARIANTS[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead commerce invariant violated: ${key}`);
        }
    }
    return QXREAD_COMMERCE_INVARIANTS;
}
//# sourceMappingURL=commerce-boundary.js.map